'use client';

import ReactPlayer from 'react-player';
import { getFacebookEmbedUrl, isFacebookVideoUrl } from '@/utils/isVideoUrl';

const YOUTUBE_HOSTS = /(youtube\.com|youtu\.be)/i;

function getYouTubeId(url = '') {
    try {
        const u = new URL(url);
        if (u.hostname.includes('youtu.be')) return u.pathname.slice(1);
        const v = u.searchParams.get('v');
        if (v) return v;
        const m = u.pathname.match(/\/(embed|shorts)\/([^/?#]+)/);
        if (m) return m[2];
    } catch (err) {
        // ignore parse error and fall back
    }
    const last = String(url).split('/').pop() || '';
    return last.split('?')[0];
}

function getYouTubeEmbedUrl(url, options = {}) {
    const id = getYouTubeId(url);
    if (!id) return '';
    const autoplay = options.autoplay ? '1' : '0';
    const mute = options.muted ? '1' : '0';
    const controls = options.controls ? '1' : '0';
    const loop = options.loop ? '1' : '0';
    const playsinline = options.playsinline ? '1' : '0';
    const params = new URLSearchParams({
        autoplay,
        mute,
        controls,
        loop,
        playsinline,
        rel: '0',
        modestbranding: '1',
    });
    if (loop === '1') params.set('playlist', id);
    return `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`;
}

function getSafeOrigin() {
    if (typeof window === 'undefined') return '';
    try {
        const origin = window.location?.origin;
        if (origin && origin !== 'null') return origin;
        const parentOrigin = window.parent?.location?.origin;
        if (parentOrigin && parentOrigin !== 'null') return parentOrigin;
    } catch (err) {
        // ignore cross-origin access errors
    }
    return '';
}

export default function VideoEmbed({
    url,
    className,
    style,
    title = 'Embedded media',
    width = '100%',
    height = '100%',
    controls = true,
    playing = false,
    muted = false,
    loop = false,
    playsinline = true,
    allowFullScreen = true,
    config,
    onError,
    onReady,
}) {
    if (!url) return null;

    const effectivePlaying = playing || !controls;
    const effectiveMuted = muted || !controls;
    const numericWidth =
        typeof width === 'number'
            ? width
            : typeof width === 'string' && /^\d+(\.\d+)?$/.test(width)
            ? Number(width)
            : undefined;
    const numericHeight =
        typeof height === 'number'
            ? height
            : typeof height === 'string' && /^\d+(\.\d+)?$/.test(height)
            ? Number(height)
            : undefined;
    const iframeLoading = controls ? 'lazy' : 'eager';
    const isYouTube = YOUTUBE_HOSTS.test(url);
    const youtubeId = isYouTube ? getYouTubeId(url) : '';

    const mergedConfig = (() => {
        if (!isYouTube) return config;
        const playerVars = {
            autoplay: effectivePlaying ? 1 : 0,
            mute: effectiveMuted ? 1 : 0,
            controls: controls ? 1 : 0,
            loop: loop ? 1 : 0,
            playsinline: playsinline ? 1 : 0,
            rel: 0,
            modestbranding: 1,
            enablejsapi: 1,
        };
        if (loop && youtubeId) playerVars.playlist = youtubeId;
        const origin = getSafeOrigin();
        if (origin) playerVars.origin = origin;

        return {
            ...config,
            youtube: {
                ...(config?.youtube || {}),
                playerVars: {
                    ...playerVars,
                    ...(config?.youtube?.playerVars || {}),
                },
            },
        };
    })();

    if (isFacebookVideoUrl(url)) {
        const src = getFacebookEmbedUrl(url, {
            autoplay: effectivePlaying,
            muted: effectiveMuted,
            width: numericWidth,
            height: numericHeight,
            allowFullScreen,
            playsinline,
        });
        return (
            <iframe
                title={title}
                src={src}
                className={className}
                style={style}
                width={width}
                height={height}
                allow="autoplay; encrypted-media; fullscreen; picture-in-picture; clipboard-write; web-share"
                allowFullScreen={allowFullScreen}
                loading={iframeLoading}
                frameBorder="0"
                scrolling="no"
                referrerPolicy="no-referrer-when-downgrade"
                onError={onError}
            />
        );
    }

    if (isYouTube) {
        const src = getYouTubeEmbedUrl(url, {
            autoplay: effectivePlaying,
            muted: effectiveMuted,
            controls,
            loop,
            playsinline,
        });
        return (
            <iframe
                title={title}
                src={src}
                className={className}
                style={style}
                width={width}
                height={height}
                allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                allowFullScreen={allowFullScreen}
                loading={iframeLoading}
                referrerPolicy="no-referrer-when-downgrade"
                onError={onError}
            />
        );
    }

    return (
        <ReactPlayer
            url={url}
            className={className}
            style={style}
            width={width}
            height={height}
            controls={controls}
            playing={effectivePlaying}
            muted={effectiveMuted}
            loop={loop}
            playsinline={playsinline}
            config={mergedConfig}
            onError={onError}
            onReady={onReady}
        />
    );
}
